import { Temporal } from "temporal-polyfill";
import { toNumber } from "./utils";

const numberFormatters = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(
  style: "decimal" | "percent" | "unit",
  minimumFractionDigits: number,
  maximumFractionDigits: number,
) {
  const key = `${style}:${minimumFractionDigits}:${maximumFractionDigits}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    // Constructing locale formatters for every displayed value is expensive
    // while the calculator updates all of its results on each quantity edit.
    formatter = new Intl.NumberFormat(undefined, {
      style,
      unit: style === "unit" ? "gram" : undefined,
      minimumFractionDigits,
      maximumFractionDigits,
    });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

export function formatPercentage(
  value: number | string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
) {
  const numericValue = toNumber(value);

  return getNumberFormatter(
    "percent",
    minimumFractionDigits,
    maximumFractionDigits,
  ).format(numericValue);
}

export function formatGrams(
  value: number | string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 1,
) {
  const numericValue = toNumber(value);

  return getNumberFormatter(
    "unit",
    minimumFractionDigits,
    maximumFractionDigits,
  ).format(numericValue);
}

export function formatKcal(
  value: number | string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
) {
  const numericValue = toNumber(value);

  return (
    getNumberFormatter(
      "decimal",
      minimumFractionDigits,
      maximumFractionDigits,
    ).format(numericValue) + " kcal"
  );
}

export function formatCelcius(
  value: number | string,
  minimumFractionDigits = 1,
  maximumFractionDigits = 1,
) {
  const numericValue = toNumber(value);

  return (
    getNumberFormatter(
      "decimal",
      minimumFractionDigits,
      maximumFractionDigits,
    ).format(numericValue) + " °C"
  );
}

export function formatFahrenheit(
  value: number | string,
  minimumFractionDigits = 1,
  maximumFractionDigits = 1,
) {
  const numericValue = toNumber(value);

  return (
    getNumberFormatter(
      "decimal",
      minimumFractionDigits,
      maximumFractionDigits,
    ).format(numericValue) + " °F"
  );
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: "auto",
});

export function formatRelativeTime(value: string) {
  let instant: Temporal.Instant;

  try {
    instant = Temporal.Instant.from(value);
  } catch {
    instant = Temporal.Instant.from(new Date(value).toISOString());
  }

  let now: Temporal.ZonedDateTime;
  try {
    now = Temporal.Now.zonedDateTimeISO();
  } catch {
    // Some browsers cannot resolve the system timezone through Intl.
    now = Temporal.Now.zonedDateTimeISO("UTC");
  }
  const then = instant.toZonedDateTimeISO(now.timeZoneId);
  const duration = now.until(then, {
    largestUnit: "year",
    smallestUnit: "minute",
    roundingIncrement: 1,
    roundingMode: "halfExpand",
  });

  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [duration.years, "year"],
    [duration.months, "month"],
    [duration.weeks, "week"],
    [duration.days, "day"],
    [duration.hours, "hour"],
    [duration.minutes, "minute"],
  ];

  const match = units.find(([amount]) => Math.abs(amount) >= 1);
  if (!match) {
    return "just now";
  }

  return relativeTimeFormatter.format(...match);
}
